import React, { useState, useEffect, useMemo } from 'react';
import DraftOrdersList from '../components/DraftOrdersList';
import PortalSkeleton from '../components/other/PortalSkeleton';
import Error from '../components/other/Error';
import EmptyState from '../components/other/EmptyState';
import ModalMain from '../components/modals/ModalMain';

import { getToken } from '../utils/auth';
import { fetchDraftOrders } from '../api/draftOrders';

import { SORT_OPTIONS, FILTER_OPTIONS, DEFAULT_DRAFT_ORDER_LIMIT } from '../../utils/constants';

function DraftOrdersListPage() {
  const [draftOrders, setDraftOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modal, setModal] = useState({ show: false, content: null });

  const [sortBy, setSortBy] = useState('createdAt');
  const [sortDir, setSortDir] = useState('desc');
  const [filterBy, setFilterBy] = useState('all');

  const [nextCursor, setNextCursor] = useState(null);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => {
    loadOrders();
  }, []);

  const loadOrders = async () => {
    const { token } = getToken();
    
    if (!token) {
      setError('No authentication token found. Please use the link from your email.');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const data = await fetchDraftOrders(token, { limit: DEFAULT_DRAFT_ORDER_LIMIT });
      setDraftOrders(data.draftOrders || []);
      setNextCursor(data.pageInfo?.endCursor ?? null);
      setHasNextPage(!!data.pageInfo?.hasNextPage);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  async function loadMore() {
    if (!hasNextPage || loadingMore) return;
    const { token } = getToken();
    if (!token) return;
    try {
      setLoadingMore(true);
      const data = await fetchDraftOrders(token, { cursor: nextCursor, limit: DEFAULT_DRAFT_ORDER_LIMIT });
      setDraftOrders(prev => [...prev, ...(data.draftOrders || [])]);
      setNextCursor(data.pageInfo?.endCursor ?? null);
      setHasNextPage(!!data.pageInfo?.hasNextPage);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingMore(false);
    }
  }

  const handleOrderRemoved = (orderId) => {
    setDraftOrders(draftOrders.filter(o => o.id !== orderId));
  };

  const showModal = (content) => {
    setModal({ show: true, content });
  };

  const closeModal = () => {
    setModal({ show: false, content: null });
  };

  const filteredAndSortedDraftOrders = useMemo(() => {
    let list = [...(draftOrders || [])];

    if (filterBy !== 'all') {
      const now = new Date();
      list = list.filter((draftOrder) => {
        const hasExpiration = !!draftOrder.expirationDate;
        const expDate = hasExpiration ? new Date(draftOrder.expirationDate) : null;
        const isExpired = hasExpiration && expDate < now;

        switch (filterBy) {
          case 'pending':
            // Treat open quotes and pending invoices as "pending", but exclude expired ones
            return (
              !isExpired &&
              (draftOrder.status === 'PENDING' || draftOrder.status === 'OPEN_QUOTE')
            );
          // case 'approved':
          //   return draftOrder.status === 'APPROVED';
          case 'declined':
            return draftOrder.declined;
          case 'expired':
            // return !!draftOrder.order?.cancelledAt && isExpired;
            return isExpired &&
              (draftOrder.status === 'PENDING' || draftOrder.status === 'OPEN_QUOTE')
          case 'cancelled':
            return !!draftOrder.order?.cancelledAt;
          case 'all_quotes':
            return draftOrder.status === 'PENDING' || draftOrder.status === 'OPEN_QUOTE';
          case 'all_orders':
            return draftOrder.status === 'COMPLETED';
          case 'completed':
            // Completed quotes whose associated order is not cancelled
            return draftOrder.status === 'COMPLETED' && !draftOrder.order?.cancelledAt;
          default:
            return true;
        }
      });
    }

    const key = sortBy;
    const isDateKey = key === 'createdAt' || key === 'updatedAt';
    const isNumericKey = key === 'totalPrice';

    list.sort((a, b) => {
      const va = isDateKey
        ? (a[key] ? new Date(a[key]).getTime() : 0)
        : isNumericKey
          ? (a[key] != null ? parseFloat(a[key]) || 0 : 0)
          : (a[key] || '').toLowerCase();
      const vb = isDateKey
        ? (b[key] ? new Date(b[key]).getTime() : 0)
        : isNumericKey
          ? (b[key] != null ? parseFloat(b[key]) || 0 : 0)
          : (b[key] || '').toLowerCase();

      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;

      // Tie‑breaker: sort by name if available so equal dates are consistently ordered
      const nameA = (a.name || '').toLowerCase();
      const nameB = (b.name || '').toLowerCase();
      if (nameA < nameB) return -1;
      if (nameA > nameB) return 1;
      return 0;
    });

    return list;
  }, [draftOrders, sortBy, sortDir, filterBy]);

  const handleSortChange = (newSortBy) => {
    if (newSortBy === sortBy) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      const opt = SORT_OPTIONS.find(o => o.value === newSortBy);
      setSortBy(newSortBy);
      setSortDir(opt?.dirDefault || 'asc');
    }
  };

  const handleFilterChange = (newFilterBy) => {
    setFilterBy(newFilterBy);
  };

  const isDefaultSort = sortBy === 'createdAt' && sortDir === 'desc';
  const isDefaultFilter = filterBy === 'all';
  const shouldShowSorting = draftOrders.length > 2 || !isDefaultSort || !isDefaultFilter;

  return (
    
    <main className="">
      {loading && <PortalSkeleton variant="list" />}
      
      {error && (
        <Error 
          message={error} 
          onRetry={loadOrders}
        />
      )}
      
      {!loading && !error && draftOrders.length === 0 && <EmptyState />}
      
      {!loading && !error && draftOrders.length > 0 && (
        <DraftOrdersList 
          draftOrders={filteredAndSortedDraftOrders}
          onOrderRemoved={handleOrderRemoved}
          showModal={showModal}
          sortBy={sortBy}
          sortDir={sortDir}
          sortOptions={SORT_OPTIONS}
          onSortChange={handleSortChange}
          filterBy={filterBy}
          filterOptions={FILTER_OPTIONS}
          onFilterChange={handleFilterChange}
          showSorting={shouldShowSorting}
          hasNextPage={hasNextPage}
          loadingMore={loadingMore}
          onLoadMore={loadMore}
        />
      )}

      {modal.show && (
        <ModalMain onClose={closeModal}>
          {modal.content}
        </ModalMain>
      )}
    </main>

      
    
  );
}

export default DraftOrdersListPage;
